import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { GitBranch, Wallet, Users, FileBarChart, LayoutDashboard, Sparkles } from 'lucide-react';
import { PRODUCT_NAME, PRODUCT_TAGLINE } from '../../lib/tenant';
import {
  LedgerScreenMockup,
  FinanceDeskMockup,
  CustomerProfileMockup,
  ReportsMockup,
  MasterDashboardMockup,
} from './AppScreenMockups';

type Chapter = {
  id: string;
  eyebrow: string;
  title: string;
  headline: string;
  body: string;
  bullets: string[];
  icon: LucideIcon;
  accent: string;
  gradient: string;
  Mockup: React.ComponentType;
};

const CHAPTERS: Chapter[] = [
  {
    id: 'ledgers',
    eyebrow: 'Flagship · Multi-branch',
    title: 'Branch ledgers',
    headline: 'One organization. Unlimited books. Instant context switch.',
    body:
      'Every shop location gets its own ledger — separate logins, scoped collections, and branch-level reports. HQ sees everything; field staff see only their book.',
    bullets: [
      'Create ledgers from Settings in seconds',
      'Disable a branch while preserving full history',
      'Collections & reports scoped to active ledger',
      'Owners switch context in one tap',
    ],
    icon: GitBranch,
    accent: 'text-violet-400',
    gradient: 'from-violet-600/20 via-indigo-600/10 to-transparent',
    Mockup: LedgerScreenMockup,
  },
  {
    id: 'finance',
    eyebrow: 'Finance desk',
    title: 'Hire-purchase origination',
    headline: 'HP workflow built for Indian vehicle finance shops.',
    body:
      'Guided origination wizard, service-line presets, live EMI schedules, branded loan PDFs, seized-vehicle register, and audit-safe closure — all in one desk.',
    bullets: [
      'Service-line, bank & finance-default presets',
      'Loan application PDF with your letterhead',
      'EMI ledger with settlement tracking',
      'Close facility only when fully paid',
    ],
    icon: Wallet,
    accent: 'text-emerald-400',
    gradient: 'from-emerald-600/20 via-teal-600/10 to-transparent',
    Mockup: FinanceDeskMockup,
  },
  {
    id: 'customers',
    eyebrow: 'Customer intelligence',
    title: 'Borrower 360°',
    headline: 'Every facility, every EMI, one complete profile.',
    body:
      'StatsCard-style KPIs, overdue drill-down, mobile record payment, WhatsApp reminders via Meta API, and optional customer portal — designed for collections teams on the move.',
    bullets: [
      'Per-facility payment, seize & refinance',
      'Overdue breakdown with missed-date view',
      'WhatsApp nudges configurable days before due',
      'Profile export for audits & sign-off',
    ],
    icon: Users,
    accent: 'text-cyan-400',
    gradient: 'from-cyan-600/20 via-blue-600/10 to-transparent',
    Mockup: CustomerProfileMockup,
  },
  {
    id: 'reports',
    eyebrow: 'Operational discipline',
    title: 'Day-end reports',
    headline: 'Period KPIs without Excel chaos.',
    body:
      'Finance created, hand loans, closures, seizures, collections, and outstanding — with flexible period filters, service-line controls, and branded PDF + Excel exports.',
    bullets: [
      'Finance & hand loan created / closed sections',
      'Collections roll-ups with drill-down filters',
      'Per-section export for sign-off workflows',
      'Mobile-friendly stat cards throughout',
    ],
    icon: FileBarChart,
    accent: 'text-amber-400',
    gradient: 'from-amber-600/20 via-orange-600/10 to-transparent',
    Mockup: ReportsMockup,
  },
  {
    id: 'master',
    eyebrow: 'Owner command center',
    title: 'Accounting master',
    headline: 'Portfolio KPIs, trends, and net position at a glance.',
    body:
      'Total borrowers, collected EMIs, closed HPs, outstanding, consultancy profit, expenses, and net position — with live charts and one-click drill-down from every stat.',
    bullets: [
      'Collections trend & portfolio split charts',
      'Consultancy inventory when module enabled',
      'Finance income, expenses & net position',
      'Top borrower concentration visibility',
    ],
    icon: LayoutDashboard,
    accent: 'text-emerald-400',
    gradient: 'from-emerald-600/20 via-green-600/10 to-transparent',
    Mockup: MasterDashboardMockup,
  },
];

function FeatureChapter({ chapter, index }: { chapter: Chapter; index: number }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const reversed = index % 2 === 1;

  return (
    <article
      ref={ref}
      id={index === 0 ? 'features' : undefined}
      className="relative border-t border-white/[0.06] py-20 lg:py-32"
    >
      <div
        className={`pointer-events-none absolute inset-0 bg-gradient-to-b ${chapter.gradient}`}
        aria-hidden
      />

      <div className="container relative mx-auto px-4">
        <div
          className={`mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2 lg:gap-20 ${
            reversed ? 'lg:[&>*:first-child]:order-2' : ''
          }`}
        >
          <motion.div
            initial={{ opacity: 0, x: reversed ? 40 : -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-xl"
          >
            <div className="flex items-center gap-2">
              <chapter.icon className={`h-4 w-4 ${chapter.accent}`} />
              <span className={`text-[11px] font-bold uppercase tracking-[0.25em] ${chapter.accent}`}>
                {chapter.eyebrow}
              </span>
            </div>

            <p className="mt-3 text-sm font-medium text-slate-500">{chapter.title}</p>
            <h3 className="mt-2 font-display text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl lg:text-[2.75rem]">
              {chapter.headline}
            </h3>
            <p className="mt-5 text-base leading-relaxed text-slate-400 sm:text-lg">{chapter.body}</p>

            <ul className="mt-8 space-y-3">
              {chapter.bullets.map((b, i) => (
                <motion.li
                  key={b}
                  initial={{ opacity: 0, x: -16 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.15 + i * 0.08 }}
                  className="flex items-start gap-3 text-sm text-slate-300 sm:text-base"
                >
                  <span className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-r from-emerald-400 to-cyan-400`} />
                  {b}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <div className="relative">
            <div className="ef-launch-chapter__frame">
              <chapter.Mockup />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function ProductLaunchShowcase() {
  const headerRef = useRef(null);
  const inView = useInView(headerRef, { once: true, margin: '-60px' });

  return (
    <section className="relative">
      <div ref={headerRef} className="container mx-auto px-4 pb-4 pt-24 lg:pt-32">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-4xl text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            <Sparkles className="h-3 w-3 text-emerald-400" />
            Product tour
          </span>
          <p className="mt-5 text-xs font-bold uppercase tracking-[0.25em] text-emerald-400">{PRODUCT_TAGLINE}</p>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl">
            See{' '}
            <span className="bg-gradient-to-r from-white via-emerald-100 to-cyan-200 bg-clip-text text-transparent">
              {PRODUCT_NAME}
            </span>{' '}
            in action
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
            Scroll through real workspace screens — branch ledgers, finance desk, customer profiles, reports, and your
            owner dashboard. This is the product, not a template.
          </p>
        </motion.div>
      </div>

      {CHAPTERS.map((chapter, i) => (
        <FeatureChapter key={chapter.id} chapter={chapter} index={i} />
      ))}
    </section>
  );
}
